import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useCurrency } from '../../hooks/useCurrency';
import { formatMoney } from '../utils/UnitConversion.utils';

const CHROME_URL = 'https://chrome.google.com/webstore/detail/MoonSkins.GG';
const FIREFOX_URL = 'https://addons.mozilla.org/en-US/firefox/addon/MoonSkinsGG';

const TradingTools = () => {
  const { t } = useTranslation();
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const { convertFromUSD } = useCurrency();

  // Thresholds in USD
  const TIER_1_USD = 10000;
  const TIER_2_USD = 50000;
  const TIER_MAX_USD = 100000;
  const MAX_INPUT_USD = 10000000;

  // Global conversion rate
  const conversionRate = useMemo(() => convertFromUSD(1), [convertFromUSD]);

  // Default values: 100 price and 1000 volume
  const [displayAmount, setDisplayAmount] = useState<number>(100);
  const [displayVolume, setDisplayVolume] = useState<number>(1000);
  const [isEarlyUser, setIsEarlyUser] = useState<boolean>(false);

  // Back-calculate USD for fee logic
  const amountInUSD = useMemo(() => displayAmount / conversionRate, [displayAmount, conversionRate]);
  const volumeInUSD = useMemo(() => displayVolume / conversionRate, [displayVolume, conversionRate]);

  // Converted thresholds for UI logic
  const convertedMaxTierValue = useMemo(() => TIER_MAX_USD * conversionRate, [conversionRate]);
  const convertedMaxInputLimit = useMemo(() => MAX_INPUT_USD * conversionRate, [conversionRate]);

  // Threshold string without decimals for the description text
  const convertedMaxTierString = useMemo(() => {
    const formatted = formatMoney(convertFromUSD(TIER_MAX_USD));
    return formatted.split(/[.,]/)[0]; // Remove decimals regardless of locale
  }, [convertFromUSD]);

  const handleAmountChange = (val: number) => {
    if (val > convertedMaxInputLimit) {
      setDisplayAmount(convertedMaxInputLimit);
    } else if (val < 0) {
      setDisplayAmount(0);
    } else {
      setDisplayAmount(val);
    }
  };

  // Fee Logic: 1.35% if manual toggle OR volume threshold reached
  const salesFeePercent = useMemo(() => {
    if (isEarlyUser || volumeInUSD >= TIER_MAX_USD) return 1.35;
    return 1.85;
  }, [isEarlyUser, volumeInUSD]);

  const withdrawFeePercent = useMemo(() => {
    if (volumeInUSD >= TIER_MAX_USD) return 0.5;
    if (volumeInUSD >= TIER_2_USD) return 1.0;
    if (volumeInUSD >= TIER_1_USD) return 1.5;
    return 2.0;
  }, [volumeInUSD]);

  const salesFeeAmountUSD = (amountInUSD * salesFeePercent) / 100;
  const withdrawFeeAmountUSD = (amountInUSD * withdrawFeePercent) / 100;

  return (
      <section className="max-w-[1600px] mx-auto px-6 md:px-8 py-8 text-left">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Left Side: Extensions */}
          <div className="bg-neutral-50 dark:bg-neutral-900/20 border border-neutral-200 dark:border-white/5 rounded-3xl shadow-xl p-10 backdrop-blur-sm flex flex-col justify-between">
            <div>
              <h3 className="text-3xl font-black mb-4 text-light-text dark:text-dark-text">
                {t('landing_page.trading_tools.extension.title')}
              </h3>
              <p className="text-gray-500 dark:text-slate-400 text-lg mb-8 max-w-md">
                {t('landing_page.trading_tools.extension.description')}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <a href={CHROME_URL} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[160px] flex flex-col items-center gap-3 p-6 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-2xl hover:bg-neutral-100 dark:hover:bg-white/10 hover:border-dark-primary/50 transition-all group">
                <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg" alt="Chrome" className="w-12 h-12" />
                <span className="font-bold text-gray-700 dark:text-slate-200 group-hover:text-dark-primary dark:group-hover:text-white">
                {t('landing_page.trading_tools.extension.chrome')}
              </span>
              </a>
              <a href={FIREFOX_URL} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[160px] flex flex-col items-center gap-3 p-6 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-2xl hover:bg-neutral-100 dark:hover:bg-white/10 hover:border-dark-primary/50 transition-all group">
                <img src="https://upload.wikimedia.org/wikipedia/commons/a/a0/Firefox_logo%2C_2019.svg" alt="Firefox" className="w-12 h-12" />
                <span className="font-bold text-gray-700 dark:text-slate-200 group-hover:text-dark-primary dark:group-hover:text-white">
                {t('landing_page.trading_tools.extension.firefox')}
              </span>
              </a>
            </div>
          </div>

          {/* Right Side: Fee Calculator */}
          <div className="bg-neutral-50 dark:bg-neutral-900/20 border border-neutral-200 dark:border-white/5 rounded-3xl shadow-xl p-10 backdrop-blur-sm flex flex-col md:flex-row gap-10">
            <div className="flex-1">
              <h3 className="text-2xl font-black text-light-text dark:text-dark-text mb-6">
                {t('landing_page.trading_tools.fee_calculator.title')}
              </h3>

              <div className="space-y-6">
                {/* Sale Amount Input */}
                <div>
                  <label className="block text-gray-500 dark:text-slate-500 font-bold text-xs uppercase tracking-widest mb-2">
                    {t('landing_page.trading_tools.fee_calculator.sale_amount')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 font-medium">{currencySymbol}</span>
                    <input
                        type="number"
                        value={displayAmount}
                        onChange={(e) => handleAmountChange(Number(e.target.value))}
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none w-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-white/10 rounded-xl py-3 pl-10 pr-4 font-bold text-xl text-light-text dark:text-dark-text focus:outline-none focus:border-dark-primary/50 transition-colors"
                    />
                  </div>
                </div>

                {/* Volume Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-gray-500 dark:text-slate-500 font-bold text-xs uppercase tracking-widest">
                      {t('landing_page.trading_tools.fee_calculator.sale_volume')}
                    </label>
                    <span className="text-light-text dark:text-white font-bold">
                    {currencySymbol}{displayVolume.toLocaleString()}{displayVolume >= convertedMaxTierValue ? '+' : ''}
                  </span>
                  </div>
                  <input
                      type="range"
                      min="0"
                      max={convertedMaxTierValue}
                      step="100"
                      value={displayVolume}
                      onChange={(e) => setDisplayVolume(Number(e.target.value))}
                      className="w-full h-1.5 bg-neutral-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-dark-primary"
                  />
                </div>

                {/* Status Toggle */}
                <div className="flex items-center justify-between p-4 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-xl">
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-bold text-light-text dark:text-dark-text">
                      {t('landing_page.trading_tools.fee_calculator.early_supporter')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-500 leading-relaxed">
                      {t('landing_page.trading_tools.fee_calculator.early_supporter_desc', {
                        symbol: currencySymbol,
                        volume: convertedMaxTierString
                      })}
                    </p>
                  </div>
                  <button
                      onClick={() => setIsEarlyUser(!isEarlyUser)}
                      className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out cursor-pointer ${isEarlyUser ? 'bg-dark-primary' : 'bg-neutral-300 dark:bg-neutral-700'}`}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${isEarlyUser ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Display */}
            <div className="w-full md:w-56 flex flex-col justify-center gap-6 border-t md:border-t-0 md:border-l border-neutral-200 dark:border-white/5 pt-6 md:pt-0 md:pl-10">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-slate-400">{t('landing_page.trading_tools.fee_calculator.sales_fee')}</span>
                  <span className="bg-neutral-200 dark:bg-white/5 px-2 py-0.5 rounded text-[10px] text-gray-600 dark:text-slate-300 font-bold">{salesFeePercent}%</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-medium dark:text-neutral-400 self-center">{currencySymbol}</span>
                  <span className="text-xl font-black text-dark-primary">{formatMoney(convertFromUSD(salesFeeAmountUSD))}</span>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-slate-400">{t('landing_page.trading_tools.fee_calculator.withdraw_fee')}</span>
                  <span className="bg-neutral-200 dark:bg-white/5 px-2 py-0.5 rounded text-[10px] text-gray-600 dark:text-slate-300 font-bold">{withdrawFeePercent}%</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-medium dark:text-neutral-400 self-center">{currencySymbol}</span>
                  <span className="text-xl font-black text-dark-primary">{formatMoney(convertFromUSD(withdrawFeeAmountUSD))}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-200 dark:border-white/5">
                <span className="text-[10px] uppercase font-bold text-gray-400 dark:text-slate-500 tracking-wider">Total Fees</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-medium dark:text-neutral-400 self-center">{currencySymbol}</span>
                  <p className="text-2xl font-black text-light-text dark:text-white">
                    {formatMoney(convertFromUSD(salesFeeAmountUSD + withdrawFeeAmountUSD))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};

export default TradingTools;